import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
} from "@mui/material";
import React, { FC, useContext, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { APIS_KEYS, useCalendar } from "@services";
import { useQueryClient } from "@tanstack/react-query";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import { useSetRecoilState } from "recoil";
import { PopupOverlayNewCalender } from "@store/index";

const POPReservationCancellation: FC<{ id?: string }> = ({ id }) => {
  const setPopupOverlayNewCalender = useSetRecoilState(PopupOverlayNewCalender)

	const handleCloseDialog = () => {
		setPopupOverlayNewCalender(undefined)
	}

  const queryClient = useQueryClient();

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = React.useState<number | undefined>(undefined);
  const [collected, setCollected] = React.useState<number | undefined>(
    undefined
  );
  const [checked, setChecked] = React.useState(false);
  const { showSuccess } = useContext(ToastMessageContext);

  const { ReservationUpdate } = useCalendar();
  const updateReservation = ReservationUpdate({
    onSuccess: () => {
      let message =
        language == "ar"
          ? "تم تعديل الحجز بنجاح"
          : "The booking has been successfully updated.";

      showSuccess(message);
      queryClient.invalidateQueries({
        queryKey: [APIS_KEYS.GETMANUALRESERVATION],
      });
      queryClient.invalidateQueries({
        queryKey: [APIS_KEYS.GETCALENDERDATA],
      });
    },
  });

  const handleClose = () => setOpen(false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReason(Number((event.target as HTMLInputElement).value));
  };

  const handleChangeCollected = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCollected(Number((event.target as HTMLInputElement).value));
  };

  const handleSubmit = async () => {
    await updateReservation
      .mutateAsync({
        reservationId: id,
        statusType: reason,
        reasonCode: collected,
      })
      .then(() => {
        setOpen(false);
        handleCloseDialog();
      });
  };

  return (
    <div>
      <Button
        sx={{
          border: "1px solid #5B3FFF",
          borderRadius: 20,
          px: 5,
        }}
        onClick={() => setOpen(true)}
      >
        {t("BOOKING.REMOVE_RESERVATION_FROM_SYNC")}
      </Button>
      <Dialog maxWidth="xl" open={open}>
        <DialogTitle className="text-end">
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: "absolute",
              left: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col">
            <FormControl>
              <FormLabel sx={{ m: 0 }}>
                <div className="flex gap-2 items-center my-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M6.6665 1.66675V5.00008"
                      stroke="#5B2FF9"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13.3335 1.66675V5.00008"
                      stroke="#5B2FF9"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M15.8333 3.33325H4.16667C3.24619 3.33325 2.5 4.07944 2.5 4.99992V16.6666C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6666V4.99992C17.5 4.07944 16.7538 3.33325 15.8333 3.33325Z"
                      stroke="#5B2FF9"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M2.5 8.33325H17.5"
                      stroke="#5B2FF9"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <p className="m-0 text-base">
                    {t("BOOKING.REMOVE_RESERVATION_REASON")}
                  </p>
                </div>
              </FormLabel>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={reason}
                onChange={handleChange}
              >
                <FormControlLabel
                  value={1}
                  control={<Radio />}
                  label={t("BOOKING.NO_SHOW_GUEST")}
                  sx={{
                    border: "1px solid #E8E8E8",
                    borderRadius: "16px",
                    margin: 0,
                    px: 0,
                    my: 1,
                  }}
                />
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label={t("BOOKING.EARLY_CHECKOUT_GUEST")}
                  sx={{
                    border: "1px solid #E8E8E8",
                    borderRadius: "16px",
                    margin: 0,
                    px: 0,
                    mb: 1,
                  }}
                />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel sx={{ m: 0 }}>
                <div className="flex gap-2 items-center my-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_4229_2906)">
                      <path
                        d="M9.99984 18.3334C14.6022 18.3334 18.3332 14.6025 18.3332 10.0001C18.3332 5.39771 14.6022 1.66675 9.99984 1.66675C5.39746 1.66675 1.6665 5.39771 1.6665 10.0001C1.6665 14.6025 5.39746 18.3334 9.99984 18.3334Z"
                        stroke="#5B2FF9"
                        stroke-width="1.66667"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M10 6.66675V10.0001"
                        stroke="#5B2FF9"
                        stroke-width="1.66667"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M10 13.3333H10.0083"
                        stroke="#5B2FF9"
                        stroke-width="1.66667"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_4229_2906">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <p className="m-0 text-base">
                    {t("BOOKING.COLLECTED_RESERVATION")}
                  </p>
                </div>
              </FormLabel>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={collected}
                onChange={handleChangeCollected}
              >
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label={t("BOOKING.COLLECTED")}
                  sx={{
                    border: "1px solid #E8E8E8",
                    borderRadius: "16px",
                    margin: 0,
                    px: 0,
                    my: 1,
                  }}
                />
                <FormControlLabel
                  value={1}
                  control={<Radio />}
                  label={t("BOOKING.NOT_COLLECTED")}
                  sx={{
                    border: "1px solid #E8E8E8",
                    borderRadius: "16px",
                    margin: 0,
                    px: 0,
                    mb: 1,
                  }}
                />
              </RadioGroup>
            </FormControl>

            <div className="flex gap-2 bg-[#5B3FFF14] p-3 px-5 rounded-xl mt-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  opacity="0.5"
                  d="M11 6C11 8.7614 8.7614 11 6 11C3.23857 11 1 8.7614 1 6C1 3.23857 3.23857 1 6 1C8.7614 1 11 3.23857 11 6Z"
                  fill="#5B3FFF"
                />
                <path
                  d="M6 8.875C6.2071 8.875 6.375 8.7071 6.375 8.5V5.5C6.375 5.2929 6.2071 5.125 6 5.125C5.7929 5.125 5.625 5.2929 5.625 5.5V8.5C5.625 8.7071 5.7929 8.875 6 8.875Z"
                  fill="#5B3FFF"
                />
                <path
                  d="M6 3.5C6.27615 3.5 6.5 3.72385 6.5 4C6.5 4.27614 6.27615 4.5 6 4.5C5.72385 4.5 5.5 4.27614 5.5 4C5.5 3.72385 5.72385 3.5 6 3.5Z"
                  fill="#5B3FFF"
                />
              </svg>
              <div>
                <p className="text-[#1A1A1A] text-sm font-bold m-0">
                  {t("BOOKING.OPEN_DATE")}
                </p>
                <p className="text-[#7A7A7A] font-normal text-xs">
                  {t("BOOKING.MAY_RESERVATION")}
                </p>
              </div>
            </div>

            <FormControlLabel
              control={<Checkbox />}
              sx={{
                mx: 0,
                mt: 2,
              }}
              label={t("BOOKING.CONFIRM_REMOVE_RESERVATION")}
              onChange={() => setChecked(!checked)}
              checked={checked}
            />
          </div>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid #F0F0F0",
            py: 3,
          }}
        >
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{ width: "50%", mx: 1 }}
            disabled={!checked || !reason || !collected}
          >
            {t("BOOKING.REMOVE_RESERVATION_AND_OPEN_NIGHTS")}
          </Button>
          <Button
            onClick={handleClose}
            sx={{ border: "1px solid #E4E4E7", width: "50%", mx: 1 }}
          >
            {t("BOOKING.CANCEL")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default POPReservationCancellation;
